import { Input } from "@legal-500/legal500-design-system";
import { useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { CmsImageUploadField } from "../../../components/CMS/CmsImageUploadField/CmsImageUploadField";
import { RichTextField, useUploadTracker } from "./blockEditorParts";
import { withoutEmptySection } from "./landingEditorParts";
import { SectionFields, type SectionFormValues, toSectionFormValues } from "./SectionFields";
import type { BlockContentErrors, VideoContent } from "./types";
import { useContentChange } from "./useContentChange";
import styles from "../CMS.module.css";

type VideoEditorProps = {
  value: VideoContent;
  onChange: (value: VideoContent) => void;
  errors: BlockContentErrors;
  onUploadingChange?: (uploading: boolean) => void;
};

type VideoFormValues = SectionFormValues & {
  intro: string;
  url: string;
  title: string;
  caption: string;
  poster: string;
};

export function VideoEditor({ value, onChange, errors, onUploadingChange }: VideoEditorProps) {
  const [initial] = useState(value);
  const { control, register, watch } = useForm<VideoFormValues>({
    defaultValues: {
      ...toSectionFormValues(value),
      intro: value.intro ?? "",
      url: value.url,
      title: value.title,
      caption: value.caption ?? "",
      poster: value.poster ?? "",
    },
  });
  const { report } = useUploadTracker(onUploadingChange);
  useContentChange(watch, onChange, values =>
    withoutEmptySection(
      { ...initial, ...values, url: values.url.trim(), title: values.title.trim() },
      ["intro", "caption", "poster"],
    ),
  );

  return (
    <div className={styles.blockEditor}>
      <SectionFields register={register} idPrefix="video" errors={errors} />

      <RichTextField
        control={control}
        name="intro"
        id="video-intro"
        label="Intro (optional)"
        minHeight={120}
        emptyAsBlank
      />

      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="video-url">
          Vimeo or YouTube link <span className={styles.required}>*</span>
        </label>
        <Input
          id="video-url"
          placeholder="https://vimeo.com/123456789"
          error={errors.url}
          {...register("url")}
        />
      </div>

      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="video-title">
          Title <span className={styles.required}>*</span>
        </label>
        <Input id="video-title" error={errors.title} {...register("title")} />
      </div>

      <div className={styles.formField}>
        <label className={styles.formLabel} htmlFor="video-caption">
          Caption (optional)
        </label>
        <Input id="video-caption" {...register("caption")} />
      </div>

      <Controller
        name="poster"
        control={control}
        render={({ field }) => (
          <CmsImageUploadField
            value={field.value}
            onChange={field.onChange}
            onUploadingChange={pending => report("poster", pending)}
            label="Poster image"
            noun="poster"
            cardLabel="Video"
            previewAlt="Video poster"
            guidance="JPG, PNG, GIF or WebP, up to 20 MiB. With a poster, the player loads only when a visitor presses play."
          />
        )}
      />
    </div>
  );
}
